import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  approveMessage,
  discardMessage,
  listMessages,
  rotateInboxToken,
  updateAllowlist,
} from "@/lib/api/inbox";
import { workspaceKeys } from "@/hooks/use-workspaces";
import { findByFileHashes } from "@/lib/api/documents";

export const inboxKeys = {
  all: ["inbox"] as const,
  messages: (workspaceId: string) => [...inboxKeys.all, "messages", workspaceId] as const,
};

export function useInboundMessages(workspaceId: string | undefined) {
  return useQuery({
    queryKey: inboxKeys.messages(workspaceId ?? ""),
    queryFn: () => listMessages(workspaceId!),
    enabled: Boolean(workspaceId),
    // Emails arrive while the page is open.
    refetchInterval: 15_000,
  });
}

/** The files of waiting emails that are already a Beleg, by hash (QA-0030). */
export function useKnownFiles(workspaceId: string | undefined, hashes: string[]) {
  return useQuery({
    queryKey: [...inboxKeys.all, "known", workspaceId ?? "", [...hashes].sort()],
    queryFn: () => findByFileHashes(workspaceId!, hashes),
    enabled: Boolean(workspaceId) && hashes.length > 0,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: inboxKeys.all });
    void queryClient.invalidateQueries({ queryKey: ["documents"] });
    void queryClient.invalidateQueries({ queryKey: workspaceKeys.all });
  };
}

export function useApproveMessage() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, allowSender }: { id: string; allowSender: boolean }) =>
      approveMessage(id, allowSender),
    onSuccess: invalidate,
  });
}

export function useDiscardMessage() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: discardMessage, onSuccess: invalidate });
}

export function useUpdateAllowlist(workspaceId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (allowlist: string[]) => updateAllowlist(workspaceId, allowlist),
    onSuccess: invalidate,
  });
}

export function useRotateInboxToken(workspaceId: string) {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: () => rotateInboxToken(workspaceId), onSuccess: invalidate });
}
